import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { teamAPI } from '../services/api';
import { Team } from '../types';

const TeamDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await teamAPI.getTeam(id!);
        setTeam(res.data as Team);
      } catch (e: any) {
        setError(e.response?.data?.message || 'Failed to load team');
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!team) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">Team not found</Alert>
      </Container>
    );
  }

  const members = Array.isArray(team.members) ? (team.members as any[]) : [];

  return (
    <Container className="team-details" maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/teams')} sx={{ mb: 2 }}>
        Back to Teams
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {team.name}
          </Typography>
          {team.description && (
            <Typography variant="body2" color="text.secondary">{team.description}</Typography>
          )}
        </Box>
        <Chip label={team.isActive ? 'Active' : 'Inactive'} color={team.isActive ? 'success' : 'default'} />
      </Box>

      <Card className="team-details__members">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PeopleIcon color="action" />
            <Typography variant="h6">Team Members ({members.length})</Typography>
          </Box>

          {members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No members in this team yet.</Typography>
          ) : (
            <List>
              {members.map((m: any) => (
                <ListItem key={typeof m === 'string' ? m : m._id} className="team-details__member-item">
                  <ListItemAvatar>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={typeof m === 'object' ? m.name : 'Member'}
                    secondary={typeof m === 'object' ? m.email : ''}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default TeamDetails;
